import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, AlertCircle, Check, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { buildApiPath, API_ENDPOINTS, getApiUrl } from "../utils/api";
import LoadingLogo from "../components/LoadingLogo";

function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // Create axios instance with correct base URL
  const axiosInstance = axios.create({
    baseURL: getApiUrl(),
    timeout: 30000,
  });

  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});

  // Define verifyToken before useEffect
  const verifyToken = useCallback(async () => {
    try {
      const response = await axiosInstance.post(
        buildApiPath(API_ENDPOINTS.AUTH.VERIFY_RESET_TOKEN),
        null,
        { params: { token } },
      );

      if (response.data.status === "success") {
        setTokenValid(true);
      }
    } catch (err) {
      console.error("Token verification failed:", err);
      if (err.response?.status === 404) {
        setError(t("auth.resetPassword.linkExpired"));
      } else if (err.response?.status === 400) {
        setError(err.response.data.detail || t("auth.resetPassword.linkUsed"));
      } else {
        setError(t("auth.resetPassword.verificationFailed"));
      }
    } finally {
      setVerifying(false);
    }
  }, [t, token, axiosInstance]);

  // Verify token on component mount
  useEffect(() => {
    if (!token) {
      setError(t("auth.resetPassword.invalidToken"));
      setVerifying(false);
      return;
    }

    verifyToken();
  }, [token, t, verifyToken]);

  const validateForm = () => {
    const newErrors = {};

    // Password validation
    if (!formData.password) {
      newErrors.password = t("auth.resetPassword.passwordRequired");
    } else {
      const hasLength = formData.password.length >= 8;
      const hasUppercase = /[A-Z]/.test(formData.password);
      const hasLowercase = /[a-z]/.test(formData.password);
      const hasDigit = /\d/.test(formData.password);
      const hasSpecial = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(
        formData.password,
      );

      const checks = [
        hasLength,
        hasUppercase,
        hasLowercase,
        hasDigit,
        hasSpecial,
      ];
      const passedChecks = checks.filter(Boolean).length;

      if (!hasLength) {
        newErrors.password = t("auth.resetPassword.passwordMinLength");
      } else if (passedChecks < 3) {
        const missing = [];
        if (!hasUppercase) missing.push("uppercase letter");
        if (!hasLowercase) missing.push("lowercase letter");
        if (!hasDigit) missing.push("number");
        if (!hasSpecial) missing.push("special character");
        newErrors.password = t("auth.resetPassword.passwordRequirements", {
          requirements: missing.join(", "),
        });
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t("auth.resetPassword.confirmRequired");
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t("auth.resetPassword.passwordsNoMatch");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post(
        buildApiPath(API_ENDPOINTS.AUTH.RESET_PASSWORD),
        {
          token: token,
          new_password: formData.password,
        },
      );

      if (response.data.status === "success") {
        setSuccess(true);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login", {
            state: {
              message: t("auth.resetPassword.loginSuccessMessage"),
            },
          });
        }, 3000);
      }
    } catch (err) {
      console.error("Password reset failed:", err);
      if (err.response?.status === 400) {
        setError(
          err.response.data.detail ||
            t("auth.resetPassword.passwordRequirementsError"),
        );
      } else if (err.response?.status === 404) {
        setError(t("auth.resetPassword.linkExpired"));
      } else {
        setError(t("auth.resetPassword.resetFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Show loading while verifying token
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
        <LoadingLogo size="lg" message={t("auth.resetPassword.verifying")} />
      </div>
    );
  }

  // Show error if token is invalid
  if (!tokenValid && !verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t("auth.resetPassword.invalidLinkTitle")}
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate("/login")}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t("auth.resetPassword.goToLogin")}
              </button>
              <p className="text-sm text-gray-500">
                {t("auth.resetPassword.needHelp")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show success message
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t("auth.resetPassword.successTitle")}
            </h2>
            <p className="text-gray-600 mb-6">
              {t("auth.resetPassword.successMessage")}
            </p>
            <div className="mt-4">
              <LoadingLogo size="sm" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show password reset form
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
              <svg
                className="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">MediLens</h2>
            <p className="mt-2 text-gray-600">
              {t("auth.resetPassword.title")}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* New Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("auth.resetPassword.newPassword")}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.password ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder={t("auth.resetPassword.newPasswordPlaceholder")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 -m-1"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {t("auth.resetPassword.passwordHint")}
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("auth.resetPassword.confirmPassword")}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.confirmPassword
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder={t(
                    "auth.resetPassword.confirmPasswordPlaceholder",
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 -m-1"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t("auth.resetPassword.resetting")}
                </>
              ) : (
                t("auth.resetPassword.resetButton")
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate("/login")}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {t("auth.backToLogin")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
